import React, { useState } from 'react';
import { Calendar, MapPin, Clock, Users, Home, Plane, ListOrdered, Edit3 } from 'lucide-react';
import { NativeDateInput, NativeTimeInput } from '../ui/NativeDateInput';
import { toast } from 'react-hot-toast';
import { gameService, type Game, type LineupSource } from '../../services/gameService';

export interface CreateGameInitialValues {
  date?: string;
  opponent_name?: string;
  game_time?: string;
}

interface CoachSteveCreateGameFormProps {
  teamId: string;
  initialValues?: CreateGameInitialValues | null;
  onSuccess: (game: Game) => void;
  onCancel: () => void;
}

export const CoachSteveCreateGameForm: React.FC<CoachSteveCreateGameFormProps> = ({
  teamId,
  initialValues,
  onSuccess,
  onCancel,
}) => {
  const defaultDate = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState<string>(initialValues?.date ?? defaultDate);
  const [opponentName, setOpponentName] = useState<string>(initialValues?.opponent_name ?? '');
  const [homeAway, setHomeAway] = useState<'home' | 'away'>('home');
  const [location, setLocation] = useState<string>('');
  const [gameTime, setGameTime] = useState<string>(initialValues?.game_time ?? '');
  const [season, setSeason] = useState<string>('');
  const [lineupSource, setLineupSource] = useState<LineupSource>('depth_chart');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!date) {
      toast.error('Please select a date');
      return;
    }
    if (!opponentName.trim()) {
      toast.error('Please enter an opponent name');
      return;
    }
    setSaving(true);
    try {
      const game = await gameService.createGame({
        team_id: teamId,
        date,
        opponent_name: opponentName.trim(),
        home_away: homeAway,
        location: location.trim() || undefined,
        game_time: gameTime || undefined,
        season: season.trim() || undefined,
        lineup_source: lineupSource,
      });
      toast.success('Game created');
      onSuccess(game);
    } catch (error: unknown) {
      console.error('Error creating game:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create game');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl bg-stone-50 border border-stone-200 p-4 space-y-4">
      <p className="text-sm font-medium text-stone-700">Add a new game</p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">
              <Calendar className="inline w-3.5 h-3.5 mr-1" />
              Date <span className="text-red-500">*</span>
            </label>
            <NativeDateInput
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">
              <Users className="inline w-3.5 h-3.5 mr-1" />
              Opponent <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={opponentName}
              onChange={(e) => setOpponentName(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Opponent team name"
              required
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1">Home / Away</label>
          <div className="flex gap-3">
            <label className="flex items-center gap-1.5 cursor-pointer text-sm">
              <input
                type="radio"
                name="homeAway"
                value="home"
                checked={homeAway === 'home'}
                onChange={() => setHomeAway('home')}
                className="text-primary"
              />
              <Home className="w-4 h-4 text-stone-500" />
              Home
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer text-sm">
              <input
                type="radio"
                name="homeAway"
                value="away"
                checked={homeAway === 'away'}
                onChange={() => setHomeAway('away')}
                className="text-primary"
              />
              <Plane className="w-4 h-4 text-stone-500" />
              Away
            </label>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">
              <MapPin className="inline w-3.5 h-3.5 mr-1" />
              Location (optional)
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Venue or address"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">
              <Clock className="inline w-3.5 h-3.5 mr-1" />
              Time (optional)
            </label>
            <NativeTimeInput
              value={gameTime}
              onChange={(e) => setGameTime(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1">Season (optional)</label>
          <input
            type="text"
            value={season}
            onChange={(e) => setSeason(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="e.g. Spring 2025"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1">Lineup</label>
          <div className="flex gap-3 flex-wrap">
            <label className="flex items-center gap-1.5 cursor-pointer text-sm">
              <input
                type="radio"
                name="lineupSource"
                value="depth_chart"
                checked={lineupSource === 'depth_chart'}
                onChange={() => setLineupSource('depth_chart')}
                className="text-primary"
              />
              <ListOrdered className="w-4 h-4 text-stone-500" />
              Use depth chart
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer text-sm">
              <input
                type="radio"
                name="lineupSource"
                value="manual"
                checked={lineupSource === 'manual'}
                onChange={() => setLineupSource('manual')}
                className="text-primary"
              />
              <Edit3 className="w-4 h-4 text-stone-500" />
              Set manually
            </label>
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-3 py-2 text-sm border border-stone-300 rounded-lg text-stone-700 hover:bg-stone-100 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 px-3 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Creating…' : 'Create game'}
          </button>
        </div>
      </form>
    </div>
  );
};
